import React, { useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowLeft, Search, Loader2, Check, AlertTriangle, Wrench, WifiOff,
} from 'lucide-react';
import { api } from '../../api/client';
import ConfirmModal from '../../components/ui/ConfirmModal';
import CertWarningList from '../../components/field/CertWarningList';
import { CertDayCounter } from '../../components/CertDayCounter';
import {
  CertWarning, certBadgeLabel, isCertWarningResponse, warningsFrom,
} from '../../lib/cert-warnings';
import {
  maintenanceNoticeTitle, pickerMaintenanceWarnings, savedMaintenanceWarnings,
} from '../../lib/maintenance-warnings';
import {
  AssignmentMaintenanceWarnings, MaintenanceNoticeBody,
} from '../../components/equipment/AssignmentMaintenanceWarnings';
import { useSyncStore } from '../../lib/offline/sync-store';
import { useBack } from '../../hooks/useBack';

/**
 * The field crew's equipment picker for one job.
 *
 * ─── BADGE, NEVER HIDE OR FILTER ────────────────────────────────────────────
 *
 * Out-of-cert gear stays in the list, marked. It is not greyed, not sorted to
 * the bottom, not behind a "show anyway" toggle.
 *
 * Hiding it would be blocking by the back door. The endpoint's whole design is
 * warn-don't-block: it 409s once, takes `force: true`, and writes a
 * `cert_override_on_assign` row so the question "did anyone know this was out
 * of cert" has an answer. A picker that will not show the unit removes the
 * crew's ability to make that override — and the spider goes on the truck
 * anyway, with no row anywhere. The recorded decision is the point.
 *
 * ─── THE VERDICT IS THE SERVER'S ────────────────────────────────────────────
 *
 * `out_of_cert` and `cert_reasons` come from GET
 * /dispatch/jobs/:id/available-equipment, computed by the same `certWarningFor`
 * the write path uses. Nothing here re-derives cert state; two implementations
 * is exactly how a picker comes to grey a unit the write accepts.
 *
 * That endpoint carries `authMiddleware` only — no `requireRole` — unlike every
 * sibling in dispatch.routes.ts. Verified live with a real field_worker token:
 * 200. If a role gate is ever added there, this screen goes dark and the
 * section on FieldJobDetail is the only thing left.
 *
 * ─── MOST ROWS SHOW A DASH, AND THAT IS CORRECT ─────────────────────────────
 *
 * Cert budgets are configured on a minority of equipment — measured against
 * production, 21 of 35 rows on a representative job have no working-day figure
 * to show. `—` means "not tracked", not "failed to load". See lib/cert-days.ts.
 *
 * ─── ONLINE ONLY ────────────────────────────────────────────────────────────
 *
 * Not in the offline sync queue and must not be added to it. A write that
 * cannot be queued must fail loudly, so the submit is disabled offline with the
 * reason on screen rather than silently pretending.
 */
export default function FieldJobEquipmentScreen() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const goBack = useBack(`/field/jobs/${jobId}`);
  const queryClient = useQueryClient();
  const isOnline = useSyncStore((s) => s.isOnline);

  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string> | null>(null);
  const [pendingWarnings, setPendingWarnings] = useState<CertWarning[] | null>(null);

  const { data: job } = useQuery({
    queryKey: ['field-job', jobId],
    queryFn: () => api.employee.myJob(jobId!).then((r) => r.data.data ?? r.data),
    enabled: !!jobId,
  });

  const { data: equipment, isLoading, isError } = useQuery({
    queryKey: ['field-available-equipment', jobId],
    queryFn: () => api.dispatch.getAvailableEquipment(jobId!).then((r) => r.data.data ?? []),
    enabled: !!jobId,
  });

  // `already_assigned` seeds the selection exactly once, when the list lands.
  // Re-seeding on every render would fight the user's taps.
  const assignedIds = useMemo(
    () => new Set<string>((equipment ?? []).filter((e: any) => e.already_assigned).map((e: any) => e.id)),
    [equipment],
  );
  if (equipment && selected === null) setSelected(new Set(assignedIds));
  const picked = selected ?? new Set<string>();

  const filtered = useMemo(() => {
    const list = equipment ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((e: any) =>
      [e.name, e.serial_number, e.type, e.manufacturer].some((v: any) => (v || '').toLowerCase().includes(q)));
  }, [equipment, search]);

  const add = Array.from(picked).filter((id) => !assignedIds.has(id));
  const remove = Array.from(assignedIds).filter((id) => !picked.has(id));
  const dirty = add.length > 0 || remove.length > 0;

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev ?? assignedIds);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const invalidate = () => {
    // The office reads and the field reads are the same rows, so everything
    // that renders job equipment has to be told.
    ['field-job', 'job', 'field-available-equipment', 'available-equipment'].forEach((k) =>
      queryClient.invalidateQueries({ queryKey: [k, jobId] }));
    ['field-my-equipment', 'equipment', 'dispatch-jobs', 'field-my-jobs'].forEach((k) =>
      queryClient.invalidateQueries({ queryKey: [k] }));
  };

  const save = useMutation({
    mutationFn: (force: boolean) =>
      api.employee.assignJobEquipment(jobId!, { add, remove, ...(force ? { force: true } : {}) }),
    onSuccess: (res) => {
      const warnings = warningsFrom(res.data);
      // Maintenance watch: advice on a 200, never a refusal, and a separate
      // thing from the cert override above. A save carrying it is not shown as
      // a clean success.
      const maintenance = savedMaintenanceWarnings(res.data);
      setPendingWarnings(null);
      invalidate();
      if (maintenance.length > 0) {
        toast.warning(maintenanceNoticeTitle(maintenance), {
          description: <MaintenanceNoticeBody warnings={maintenance} />,
        });
      }
      if (warnings.length > 0) {
        // A 200 with warnings is a successful OVERRIDE, not an error. Say so
        // plainly — the crew just told the system it knew.
        toast.warning(
          `Assigned. ${warnings.length} out-of-cert ${warnings.length === 1 ? 'item' : 'items'} recorded as an override.`,
        );
      } else if (maintenance.length === 0) {
        toast.success('Equipment updated');
      }
      navigate(`/field/jobs/${jobId}`);
    },
    onError: (err: any) => {
      const status = err?.response?.status;
      const body = err?.response?.data;
      if (isCertWarningResponse(status, body)) {
        // 409 and NOTHING was written. The confirm re-sends the identical body
        // with force:true.
        setPendingWarnings(warningsFrom(body));
        return;
      }
      if (status === 403) {
        toast.error(body?.error || 'Field equipment assignment is not enabled for this company.');
        return;
      }
      if (status === 404) {
        toast.error('You are not on the crew for this job, or that equipment is no longer available.');
        return;
      }
      toast.error(body?.error || 'Could not update equipment');
    },
  });

  const busy = save.isPending;

  return (
    <div className="animate-fade-in min-h-full bg-fieldiq-dark pb-[var(--bottom-chrome)]">
      <div className="border-b border-border px-4 py-3 flex items-center gap-2">
        <button onClick={goBack} aria-label="Back">
          <ArrowLeft className="w-5 h-5 text-white" />
        </button>
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-foreground truncate">Equipment</h1>
          {job?.job_number && (
            <p className="text-xs text-fieldiq-muted truncate">
              {job.job_number}{job.well_name ? ` · ${job.well_name}` : ''}
            </p>
          )}
        </div>
      </div>

      <div className="px-4 py-3 space-y-3">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-fieldiq-muted" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, serial, type…"
            className="w-full bg-fieldiq-card border border-fieldiq-border rounded-xl pl-9 pr-3 py-2.5 text-white text-sm placeholder-fieldiq-muted focus:outline-none focus:border-amber-500/50"
          />
        </div>

        {!isOnline && (
          <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 rounded-xl p-3">
            <WifiOff size={14} className="text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-300">
              You are offline. Equipment changes are not queued — reconnect before saving,
              so nothing is booked that did not reach the office.
            </p>
          </div>
        )}

        {isLoading && (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" /></div>
        )}

        {/* A failed read is a refusal, not an empty state — and it must not
            offer a create affordance that would turn a read failure into a
            write failure. */}
        {isError && (
          <div className="text-center py-12">
            <AlertTriangle size={32} className="mx-auto text-amber-400 mb-3" />
            <p className="text-sm text-fieldiq-text">Could not load the equipment list.</p>
            <p className="text-xs text-fieldiq-muted mt-1">Check your connection and pull to retry.</p>
          </div>
        )}

        {!isLoading && !isError && filtered.length === 0 && (
          <div className="text-center py-12">
            <Wrench size={32} className="mx-auto text-fieldiq-muted mb-3" />
            <p className="text-sm text-fieldiq-text">
              {search ? 'No equipment matches that search.' : 'No equipment available.'}
            </p>
          </div>
        )}

        <div className="space-y-2">
          {filtered.map((eq: any) => {
            const on = picked.has(eq.id);
            const outOfCert = !!eq.out_of_cert;
            // The toggle is a button stretched over the card, not the card itself: the
            // card holds a link to the unit's current job, and a link inside a button
            // is invalid HTML. Same shape as EquipmentAssignmentScreen.
            const infoId = `field-equipment-row-${eq.id}`;
            return (
              <div
                key={eq.id}
                className={`relative w-full text-left bg-fieldiq-card border rounded-xl p-3 min-h-[44px] transition-colors ${
                  on ? 'border-fieldiq-accent' : 'border-fieldiq-border'
                }`}
              >
                <button
                  type="button"
                  onClick={() => toggle(eq.id)}
                  aria-pressed={on}
                  aria-labelledby={infoId}
                  className="absolute inset-0 w-full rounded-xl"
                />
                <div id={infoId} className="flex items-start gap-3">
                  <div className={`w-5 h-5 rounded flex-shrink-0 mt-0.5 flex items-center justify-center border ${
                    on ? 'bg-fieldiq-accent border-fieldiq-accent' : 'border-fieldiq-muted'
                  }`}>
                    {on && <Check size={14} className="text-fieldiq-dark" />}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-white">{eq.name}</span>
                      {/* THE BADGE. Never a filter, never a disable — see the
                          header block. */}
                      {outOfCert && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">
                          <AlertTriangle size={10} />
                          {certBadgeLabel(eq.cert_reasons)}
                        </span>
                      )}
                    </div>

                    <p className="text-xs text-fieldiq-muted truncate">
                      {[eq.serial_number, eq.type, eq.manufacturer].filter(Boolean).join(' · ') || '—'}
                    </p>

                    {eq.current_job_number && !eq.already_assigned && (
                      <p className="text-xs text-blue-300 mt-0.5">
                        On{' '}
                        {eq.current_job_id ? (
                          // Above the stretched toggle; the click stops here so opening the
                          // job does not also pick the unit.
                          <Link
                            to={`/field/jobs/${eq.current_job_id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="relative z-10 underline"
                          >
                            {eq.current_job_number}
                          </Link>
                        ) : (
                          eq.current_job_number
                        )}
                      </p>
                    )}

                    {/* The shared counter. A unit with no cert budget renders a
                        dash — not tracked, not missing data. */}
                    <CertDayCounter
                      daysUsed={eq.working_days_since_inspection}
                      dayLimit={eq.cert_day_limit}
                      mode="used"
                      size="xs"
                      barHeight="sm"
                      muted
                      className="mt-2"
                    />

                    {/* Maintenance watch, under the cert line. A warning, never
                        a gate — see lib/maintenance-warnings.ts. */}
                    <AssignmentMaintenanceWarnings warnings={pickerMaintenanceWarnings(eq)} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="sticky bottom-[var(--bottom-chrome)] px-4 py-3 bg-fieldiq-dark/95 backdrop-blur border-t border-fieldiq-border">
        <button
          onClick={() => save.mutate(false)}
          disabled={!dirty || busy || !isOnline}
          className="w-full min-h-[44px] rounded-xl bg-fieldiq-accent text-fieldiq-dark font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {busy && <Loader2 size={14} className="animate-spin" />}
          {dirty
            ? `Save — ${add.length} added, ${remove.length} removed`
            : 'No changes'}
        </button>
      </div>

      <ConfirmModal
        isOpen={pendingWarnings !== null}
        title="Some equipment is out of cert"
        confirmLabel="Assign anyway"
        confirmVariant="primary"
        loading={busy}
        onCancel={() => setPendingWarnings(null)}
        onConfirm={() => save.mutate(true)}
        body={
          <div className="space-y-3">
            <CertWarningList warnings={pendingWarnings ?? []} />
            <p className="text-xs text-fieldiq-muted">
              Nothing has been assigned yet. Assigning anyway is recorded against your name.
            </p>
          </div>
        }
      />
    </div>
  );
}
